import { Checkbox, Form, Input, InputRef, Select, Switch } from "antd";
import { t } from "i18next";
import * as React from "react";
import { trimRequired } from "src/common/validationRules/trimRequired";
import { MultiLangFields } from "src/components/MultiLangFields";
import { FieldsWithTitle } from "src/pages/ManagementPage/FieldsWithTitle";
import { ObjIteratorMode } from "src/pages/ManagementPage/objIterator";
import { WidthLimitedFields } from "src/pages/ManagementPage/WidthLimitedFields";
import { IdLabel } from "src/references/getIdNames";
import { AttrTypeName, isAttrDictionary } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import { ZIdName } from "src/types/ZIdName";
import { ZUid } from "src/types/zUid";
import { ZSystemAttributeType } from "src/types/ZSystemAttributeType";
import { createMassAttrEditSettingsStore } from "src/components/MassEdit/MassEditSettingsStore";
import { edAttrField } from "../../../EdAttribute";
import { newItemId } from "../../../Obj2Nodes";
import { ZRolesGroup } from "../../../roles/roleTypes";
import { getTypeIcon } from "../../../utils/typeIcons";
import { IdInfo } from "../../IdInfo";
import { validatorOfId } from "../attrValidators";
import { EditorInfo2, ViewInfo } from "../MetaInfo";
import { CopyByValueEdit } from "../MetaInfo/CopyByValueEdit";
import { createCopyByValueEditStore } from "../MetaInfo/CopyByValueEdit/createCopyByValueEditStore";
import { edItemField } from "../MetaInfo/EditorInfo2/ParamsEditorInfo2";
import { SelectDictionary, SelectDictionaryStore } from "../SelectDictionary";
import { SelectObject } from "../SelectObject";
import { SelectRoles } from "../SelectRoles";
import { AttrArticleTemplateForm } from "./ArticleTemplateInput/AttrArticleTemplateForm";
import styles from "./AttrCommonFields.module.less";
import { LinkedValuePath } from "./LinkedValuePath";
import { MassAttrEditSettings } from "../../../../../../components/MassEdit/MassEditSettings";
import {
  copyByValueTypes,
  attrTypesForUniqueValue,
} from "./attributeTypeCapabilities";

interface DataSource {
  attrTypesList: IdLabel[];
  attrTypesDict: Record<number, string>;
  roleGroups: ZRolesGroup[];
  selectDictionaryStore: SelectDictionaryStore;
  readonly currentObjectId: ZUid | null;
}

interface PropsAttrCommonFields {
  attrKey: React.Key;
  attrId: ZUid;
  attrDisplayId: ZUid;
  objectId: ZUid;
  disabled: boolean;
  dataSource: DataSource;
  objIteratorMode: ObjIteratorMode;
  systemAttrType: ZSystemAttributeType | null | undefined;
  isFieldUnique: (
    field: keyof ZAttribute,
    attrKey: React.Key,
    attrId: ZUid,
    value: unknown,
  ) => boolean;
  loadObjectOptions(): Promise<ZIdName[]>;
}

export const AttrCommonFields: React.FC<PropsAttrCommonFields> = (props) => {
  const {
    objectId,
    attrId,
    attrDisplayId,
    attrKey,
    disabled,
    dataSource,
    systemAttrType,
    isFieldUnique,
    loadObjectOptions,
    objIteratorMode,
  } = props;
  const {
    attrTypesList,
    attrTypesDict,
    roleGroups,
    selectDictionaryStore,
    currentObjectId,
  } = dataSource;
  // Установка фокуса на первое поле
  const ref1 = React.useRef<InputRef>(null);
  React.useEffect(() => {
    setTimeout(() => ref1.current?.focus(), 10);
  }, [attrKey]);
  const valueType = Form.useWatch(edAttrField.valueType);
  const typeName = attrTypesDict[valueType] ?? "";
  const typeFilter = (typeId: number): boolean =>
    isAttrDictionary(attrTypesDict[typeId]);
  const dictVisible = typeFilter(valueType);
  const objVisible =
    typeName === AttrTypeName.object ||
    typeName === AttrTypeName.childEntities ||
    typeName === AttrTypeName.financial;
  const roleIdsVisible = attrTypesDict[valueType] === AttrTypeName.usersList;
  const isLinkedValue = typeName === AttrTypeName.linkedValue;
  const isArticle = typeName === AttrTypeName.article;
  const clearByCopyVisible = typeName !== AttrTypeName.childEntities;

  const isCopyByValueRule = copyByValueTypes.has(typeName);
  const copyByValueStore = React.useMemo(() => {
    if (!!objectId && !!attrId && attrTypesDict && valueType) {
      return createCopyByValueEditStore({
        objectId,
        attrId,
        typesMap: attrTypesDict,
        valueType,
      });
    }
    return null;
  }, [objectId, attrId, attrTypesDict, valueType]);
  const showCopyByValSettings = isCopyByValueRule && copyByValueStore;
  const autofillByCopy = typeName === AttrTypeName.object;

  const attrTypesForUniqueValueVisible = attrTypesForUniqueValue.has(typeName);

  const massEditStore = React.useMemo(
    () => createMassAttrEditSettingsStore(valueType, !!systemAttrType),
    [valueType],
  );
  return (
    <div className={styles.common}>
      <WidthLimitedFields>
        <FieldsWithTitle title="Название атрибута">
          <Form.Item
            name={edAttrField.name}
            label="Системное название"
            rules={[
              trimRequired(),
              {
                validator: (_rule, value) =>
                  isFieldUnique("name", attrKey, attrId, value)
                    ? Promise.resolve()
                    : Promise.reject(Error("Название уже используется")),
              },
            ]}
          >
            <Input
              allowClear
              ref={ref1}
              disabled={disabled || attrId !== newItemId}
            />
          </Form.Item>
          <MultiLangFields basePath={[edAttrField.translations]} />
          <IdInfo dataNode={{ id: attrId, displayId: attrDisplayId }} />
        </FieldsWithTitle>

        <FieldsWithTitle title="Настройки атрибута">
          <div className={styles.typeBlock}>
            <Form.Item
              className="idAttrType"
              name={edAttrField.valueType}
              label="Тип атрибута"
              rules={[{ required: true }]}
            >
              <TypeSelect
                types={attrTypesList}
                disabled={disabled || attrId !== newItemId}
              />
            </Form.Item>
            <Form.Item
              name={edAttrField.fieldKey}
              label="Идентификатор"
              tooltip="Для программного доступа к атрибуту"
              rules={[
                validatorOfId,
                {
                  validator: (_rule, value) =>
                    isFieldUnique("fieldKey", attrKey, attrId, value)
                      ? Promise.resolve()
                      : Promise.reject(Error("Идентификатор уже использован")),
                },
              ]}
            >
              <Input allowClear disabled={disabled} />
            </Form.Item>
          </div>
          {attrTypesForUniqueValueVisible && (
            <Form.Item name={edAttrField.uniqueValue} valuePropName="checked">
              <Checkbox>Уникальное значение</Checkbox>
            </Form.Item>
          )}
          <MassAttrEditSettings store={massEditStore} />
        </FieldsWithTitle>
        {isLinkedValue && (
          <Form.Item
            name={edAttrField.pathValue}
            label="Путь к атрибуту"
            rules={[{ required: true }]}
          >
            <LinkedValuePath
              currAttrId={attrId}
              objectId={objectId}
              attrTypesDict={attrTypesDict}
            />
          </Form.Item>
        )}

        {isArticle && (
          <AttrArticleTemplateForm
            objectId={objectId}
            articleSettingsDisabled={disabled || attrId !== newItemId}
          />
        )}

        {dictVisible && (
          <Form.Item
            name={edAttrField.referenceId}
            label="Справочник"
            rules={[{ required: true }]}
            className="idReferenceToDict"
          >
            <SelectDictionary
              store={selectDictionaryStore}
              disabled={disabled}
            />
          </Form.Item>
        )}

        {objVisible && (
          <Form.Item
            name={edAttrField.referenceId}
            label={t("attrType.OBJECT_ID")}
            rules={[{ required: true }]}
          >
            <SelectObject
              loadObjectOptions={loadObjectOptions}
              disabled={disabled}
            />
          </Form.Item>
        )}

        {roleIdsVisible && (
          <Form.Item name={edAttrField.roleIds} label="Доступные группы ролей">
            <SelectRoles roleGroups={roleGroups} />
          </Form.Item>
        )}

        {objIteratorMode !== "hidden" && (
          <Form.Item
            name={edAttrField.iterator}
            label={`Является итератором ${objIteratorMode === "blocked" ? "(У данного объекта уже есть итератор)" : ""}`}
            tooltip="Означает, что будет создаваться несколько экземпляров - для каждого из выбранных значений атрибута. В объекте может быть только один итератор."
          >
            <Switch disabled={objIteratorMode !== "enabled"} />
          </Form.Item>
        )}

        {clearByCopyVisible && (
          <Form.Item
            name={edAttrField.clearByCopy}
            label="Очищать при копировании"
            tooltip="Означает, что при копировании экземпляра в копии значение атрибута будет очищено"
          >
            <Switch />
          </Form.Item>
        )}
      </WidthLimitedFields>

      <FieldsWithTitle title="Отображение атрибута">
        <div className={styles.twoCols}>
          <EditorInfo2
            params={{
              attrTypesDict,
              disabled,
              objectId,
              curAttrId: attrId,
              loadObjectOptions,
            }}
          />
          <ViewInfo
            attrTypesDict={attrTypesDict}
            currentObjectId={currentObjectId}
            loadObjectOptions={loadObjectOptions}
          />
        </div>
      </FieldsWithTitle>

      {showCopyByValSettings && (
        <Form.Item
          name={edItemField("copyByValueRule")}
          label="Наследование по значению другого атрибута"
        >
          <CopyByValueEdit store={copyByValueStore} />
        </Form.Item>
      )}

      {autofillByCopy && (
        <Form.Item
          name={edAttrField.autofillByCopy}
          label="Автозаполнение при копировании экземпляра"
          tooltip='Если "Верно", то при пользовательском копировании экземпляра данного объекта значение атрибута заполняется ссылкой на исходный экземпляр, который был скопирован'
        >
          <Switch />
        </Form.Item>
      )}
    </div>
  );
};

type PropsTypeSelect = {
  types: IdLabel[];
  value?: number;
  onChange?(v: number): void;
  disabled: boolean;
};
const TypeSelect: React.FC<PropsTypeSelect> = (props) => {
  const { types, value, onChange, disabled } = props;
  return (
    <Select
      disabled={disabled}
      value={value}
      onChange={(type) => {
        onChange?.(type);
      }}
      options={types.map(({ id, label }) => ({
        value: id,
        label: (
          <div className={styles.typeOption}>
            {getTypeIcon(id)} {label}
          </div>
        ),
      }))}
    />
  );
};
